import React, { useEffect, useRef, useState } from "react";
import { gsap, useGSAP, EASE, prefersReducedMotion } from "../../lib/gsap";
import { usePreloader } from "../../context/PreloaderContext";
import { personalInfo, experience, projects } from "../../data/portfolio";
import { scrollToSection } from "../../lib/scrollTo";
import Magnetic from "../ui/Magnetic";
import Icon from "../ui/Icon";
import Ruler from "../motion/Ruler";

const current = experience[0];

/* What he actually does, four ways. Only one is filled at a time. */
const LINES = ["AI Engineer", "ML Systems", "RAG & MCP", "Audio ML"];

const READOUTS = [
  { label: "Role", value: personalInfo.title },
  { label: "Now", value: `${current.title}, ${current.company}` },
  { label: "Working on", value: "RAG, MCP, audio models" },
  { label: "Projects", value: `${projects.length} shipped` },
];

const CYCLE_MS = 2800;

/**
 * The hero is a stack of four outlined lines with exactly one filled. The fill
 * sweeps from line to line on its own, and follows the pointer when the reader
 * takes over. The portrait sits over the type in print-style multiply, so the
 * two layers share one surface instead of sitting in separate boxes.
 */
const Hero = () => {
  const scope = useRef(null);
  const portrait = useRef(null);
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);
  const { isPreloaderDone } = usePreloader();

  /* Self-cycling until the reader points at a line */
  useEffect(() => {
    if (held || !isPreloaderDone) return undefined;
    if (prefersReducedMotion()) return undefined;

    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % LINES.length);
    }, CYCLE_MS);

    return () => window.clearInterval(id);
  }, [held, isPreloaderDone]);

  /* One entrance timeline, same curve family as the load screen */
  useGSAP(
    () => {
      if (!isPreloaderDone) return undefined;

      if (prefersReducedMotion()) {
        gsap.set("[data-hero-fade]", { opacity: 1 });
        return undefined;
      }

      const tl = gsap.timeline({ defaults: { ease: EASE.out } });

      tl.from("[data-hero-line]", { yPercent: 112, duration: 0.72, stagger: 0.06 }, 0)
        .fromTo(
          portrait.current,
          { clipPath: "inset(0% 0% 100% 0%)" },
          { clipPath: "inset(0% 0% 0% 0%)", duration: 0.8, ease: EASE.io },
          0.18
        )
        .from("[data-hero-fade]", { opacity: 0, y: 10, duration: 0.5, stagger: 0.06 }, 0.34);

      return () => tl.kill();
    },
    { scope, dependencies: [isPreloaderDone] }
  );

  /* The portrait drifts a little against the pointer — depth, not parallax */
  useGSAP(
    () => {
      const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      if (!fine || prefersReducedMotion() || !portrait.current) return undefined;

      const moveX = gsap.quickTo(portrait.current, "x", { duration: 0.9, ease: "power3.out" });
      const moveY = gsap.quickTo(portrait.current, "y", { duration: 0.9, ease: "power3.out" });

      const onMove = (e) => {
        const box = scope.current.getBoundingClientRect();
        moveX(((e.clientX - box.left) / box.width - 0.5) * -26);
        moveY(((e.clientY - box.top) / box.height - 0.5) * -18);
      };

      scope.current.addEventListener("pointermove", onMove);
      return () => scope.current?.removeEventListener("pointermove", onMove);
    },
    { scope }
  );

  return (
    <section
      id="home"
      ref={scope}
      className="chapter relative flex min-h-[100svh] flex-col justify-between overflow-hidden pt-20 pb-24 md:pb-20"
      aria-labelledby="home-title"
    >
      {/* Meta row */}
      <div className="page">
        <div className="flex items-baseline justify-between gap-6 pb-3">
          <span data-hero-fade className="t-label-strong opacity-0">
            01 — Home
          </span>
          <span data-hero-fade className="t-label opacity-0">
            {personalInfo.location}
          </span>
        </div>
        <Ruler ticks={20} />
      </div>

      {/* The stack */}
      <div className="page relative">
        <div
          className="relative z-10 flex flex-col"
          onMouseLeave={() => setHeld(false)}
          aria-hidden="true"
        >
          {LINES.map((line, i) => {
            const on = i === active;
            return (
              <span key={line} className="mask-line">
                <span
                  data-hero-line
                  className="relative block cursor-default select-none"
                  onMouseEnter={() => {
                    setHeld(true);
                    setActive(i);
                  }}
                  style={{
                    fontSize: "clamp(2.6rem, 11.5vw, 11rem)",
                    lineHeight: 0.94,
                    letterSpacing: "-0.045em",
                    fontVariationSettings: "'wdth' 104, 'wght' 700",
                    textTransform: "uppercase",
                  }}
                >
                  {/* outline layer */}
                  <span className="line-outline block">{line}</span>

                  {/* filled copy, clipped in from the left */}
                  <span
                    className="absolute inset-0 block text-fg"
                    style={{
                      clipPath: on ? "inset(0% 0% 0% 0%)" : "inset(0% 100% 0% 0%)",
                      transition: `clip-path ${on ? 620 : 380}ms cubic-bezier(0.16, 1, 0.3, 1)`,
                    }}
                  >
                    {line}
                  </span>
                </span>
              </span>
            );
          })}
        </div>

        {/* Portrait, printed over the type */}
        <div
          ref={portrait}
          className="pointer-events-none absolute right-0 top-1/2 z-0 hidden w-[26%] max-w-[19rem] -translate-y-1/2 md:block lg:right-[4%]"
          aria-hidden="true"
        >
          <img
            src="/me.webp"
            alt=""
            className="h-auto w-full"
            style={{
              filter: "grayscale(1) contrast(1.15)",
              mixBlendMode: "multiply",
            }}
          />
        </div>
      </div>

      {/* Who, and what to do next */}
      <div className="page">
        <Ruler ticks={20} className="mb-5" />

        <div className="grid-12 items-end gap-y-8">
          <div className="col-span-12 lg:col-span-6">
            <h1 data-hero-fade id="home-title" className="t-h3 text-fg opacity-0">
              {personalInfo.fullName} — {personalInfo.title}
            </h1>
            <p data-hero-fade className="t-body mt-3 opacity-0">
              I build AI systems that run on real data: research agents, retrieval, audio
              models and forecasting. Based in {personalInfo.location}.
            </p>

            <div data-hero-fade className="mt-7 flex flex-wrap items-center gap-4 opacity-0">
              <Magnetic>
                <button
                  type="button"
                  onClick={() => scrollToSection("work")}
                  className="control control-solid"
                  data-cursor-label="Work"
                >
                  See the work
                  <Icon name="arrowRight" />
                </button>
              </Magnetic>
              <Magnetic>
                <button
                  type="button"
                  onClick={() => scrollToSection("contact")}
                  className="control"
                  data-cursor-label="Contact"
                >
                  Get in touch
                </button>
              </Magnetic>
            </div>
          </div>

          <dl className="col-span-12 grid grid-cols-2 gap-x-6 gap-y-5 lg:col-span-5 lg:col-start-8 lg:grid-cols-2">
            {READOUTS.map((item) => (
              <div key={item.label} data-hero-fade className="opacity-0">
                <dt className="t-label">{item.label}</dt>
                <dd className="mt-1 text-[0.9375rem] leading-snug text-fg">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};

export default Hero;
